(ns manage.slate-editor.util
  (:require ["slate" :as slate]
            [goog.object :as gobject]
            [mate.core :as mc])
  (:require-macros [manage.slate-editor.util]))

(defn re-partition-indexed
  "Partitions a string in segments of text which are matching or not matching a regex.
   Note: It allows for empty non-matched texts around matched text for Slate's needs."
  [re s]
  (let [f (fn -indexed-re-partition [re s base-index]
            (if-some [found (mc/re-find-indexed re s)]
              (let [first-element (first found)
                    [match-index match-size match] (cond
                                                     ;; If there are no subgroups in the regex
                                                     (int? first-element)
                                                     (let [second-element (second found)]
                                                       [first-element
                                                        (count second-element)
                                                        [(+ base-index first-element) second-element]])

                                                     ;; If there are subgroups in the regex
                                                     (vector? first-element)
                                                     [(first first-element)
                                                      (count (second first-element))
                                                      (->> found
                                                           (mapv (fn [[index matched-text]]
                                                                   [(+ base-index index) matched-text])))])]

                (list* {:index base-index
                        :size  match-index
                        :text (subs s 0 match-index)}
                       {:index (+ base-index match-index)
                        :size match-size
                        :match match}
                       (lazy-seq (-indexed-re-partition re
                                                        (subs s (+ match-index match-size))
                                                        (+ base-index match-index match-size)))))
              (list {:index base-index
                     :size (count s)
                     :text s})))]
    (f re s 0)))

(defn with-inline-element-types
  "Augments `editor.isInline(element)` to return true when its type is in the collection `element-types`."
  [^js editor element-types]
  (let [original-is-inline (.-isInline editor)
        element-types-set (set element-types)]
    (doto editor
      (gobject/set "isInline" (fn [^js element]
                                (let [element-type (.-type element)]
                                  (if (contains? element-types-set element-type)
                                    true
                                    (original-is-inline element))))))))

(defn with-void-element-types
  "Augments `editor.isVoid(element)` to return true when its type is in the collection `element-types`."
  [^js editor element-types]
  (let [original-is-void (.-isVoid editor)
        element-types-set (set element-types)]
    (doto editor
      (gobject/set "isVoid" (fn [^js element]
                              (let [element-type (.-type element)]
                                (if (contains? element-types-set element-type)
                                  true
                                  (original-is-void element))))))))

(defn with-normalize-node
  "Augments `editor.normalizeNode(node-entry)` by passing through the transform function `f`."
  [^js editor f]
  (let [original-normalize-node (.-normalizeNode editor)]
    (doto editor
      (gobject/set "normalizeNode" (fn [^js node-entry]
                                     (f editor node-entry original-normalize-node))))))

(defn with-insert-text-data
  "Wraps `editor.insertTextData(...)` using the function `f`."
  [^js editor f]
  (let [original-insert-text-data (.-insertTextData editor)]
    (doto editor
      (gobject/set "insertTextData" (fn [^js data-transfer]
                                      (f editor data-transfer original-insert-text-data))))))

(defn get-parent-node
  "Same as `slate/Node.parent` but also handles the case of empty path."
  [^js editor ^js path]
  (when (seq path)
    (slate/Node.parent editor path)))

(defn get-parent-path
  "Same as `slate/Path.parent` but also handles the case of empty path."
  [^js path]
  (when (seq path)
    (slate/Path.parent path)))

(defn get-child-path [^js path child-index]
  (-> path
      js->clj
      (conj child-index)
      clj->js))

(defn ascending-paths [^js path]
  (->> (iterate get-parent-path path)
       (take-while some?)))

(defn cursor-point-when-no-selection [^js editor]
  (let [selection (.-selection editor)]
    (when (some-> selection slate/Range.isCollapsed)
      (slate/Editor.start editor selection))))

(defn find-node-path-above [^js editor ^js location node-type]
  (slate/Editor.above editor #js {:at location
                                  :match (fn [node]
                                           (= (.-type node) node-type))}))

(defn is-descendant-of-node-type? [^js editor ^js location node-type]
  (some? (find-node-path-above editor location node-type)))

(defn find-previous-node-entries-while
  [^js editor ^js path node-pred?]
  (->> (iterate (fn [[_node path]]
                  (when (some? path)
                    (let [[next-node _next-path :as next-entry] (slate/Editor.previous editor #js {:at path})]
                      (when (node-pred? next-node)
                        next-entry))))
                [nil path])
       (drop 1)
       (take-while some?)
       vec))

(defn find-next-node-entries-while
  [^js editor ^js path node-pred?]
  (->> (iterate (fn [[_node path]]
                  (when (some? path)
                    (let [[next-node _next-path :as next-entry] (slate/Editor.next editor #js {:at path})]
                      (when (node-pred? next-node)
                        next-entry))))
                [nil path])
       (drop 1)
       (take-while some?)
       vec))

(defn find-node-entries-around
  [^js editor ^js path node-match?]
  (-> []
      (into (rseq (find-previous-node-entries-while editor path node-match?)))
      (conj (slate/Editor.node editor path))
      (into (find-next-node-entries-while editor path node-match?))))

(defn compress-range
  "Returns a range where:
   - the start of the range is not on the end of a node, and
   - the end of the range is not on the start of a node."
  [^js editor ^js range]
  (let [[start end] (slate/Range.edges range)
        ;; Maybe move the start forward
        start (loop [start start]
                (if (slate/Point.equals start end)
                  start
                  (let [[_start-node start-node-path] (slate/Editor.node editor start)]
                    (if (slate/Editor.isEnd editor start start-node-path)
                      (recur (slate/Editor.after editor start #js {:distance 1, :unit "offset"}))
                      start))))
        ;; Maybe move the end backward
        end (loop [end end]
              (if (slate/Point.equals start end)
                end
                (let [[_end-node end-node-path] (slate/Editor.node editor end)]
                  (if (slate/Editor.isStart editor end end-node-path)
                    (recur (slate/Editor.before editor end #js {:distance 1, :unit "offset"}))
                    end))))]
    #js {:anchor start
         :focus end}))

(defn- normalize-to-single-paragraph [^js editor ^js node-entry original-normalize-node]
  (let [[node node-path] node-entry]
    (if (and (empty? node-path)
             (> (-> node .-children .-length) 1))
      (slate/Transforms.mergeNodes editor #js {:at #js [1]})
      (original-normalize-node node-entry))))

(defn with-single-line [^js editor]
  (-> editor
      (with-normalize-node normalize-to-single-paragraph)))

(defn toggle-mark [^js editor mark]
  (let [active-marks (js->clj (slate/Editor.marks editor))]
    (if (contains? active-marks mark)
      (slate/Transforms.unsetNodes editor
                                   mark
                                   #js {:match slate/Text.isText
                                        :split true})
      (slate/Transforms.setNodes editor
                                 (clj->js {mark true})
                                 #js {:match slate/Text.isText
                                      :split true}))))

(defn flatten-node [^js editor ^js path]
  (cond
    ;; Sometimes `slate/Transforms.wrapNodes` leaves a node with an empty children array.
    ;; This is for dealing with this buggy case.
    (let [[node _path] (slate/Editor.node editor path)]
      (and (not (slate/Text.isText node))
           (empty? (.-children node))))
    (slate/Transforms.delete editor #js {:at path})

    :else
    (slate/Transforms.unwrapNodes editor #js {:at path
                                              :mode "all"
                                              :match (fn [_node match-path]
                                                       (or (slate/Path.equals path match-path)
                                                           (slate/Path.isAncestor path match-path)))})))

(defn children-nodes-info [^js editor ^js path]
  (loop [result []
         index 0
         child-entries (seq (slate/Node.children editor path))] ; seq is needed, to change the JS generator into a sequence
    (if (seq child-entries)
      (let [[child-node child-path] (first child-entries)
            size (count (slate/Node.string child-node))]
        (recur (conj result {:index index
                             :size  size
                             :path  child-path
                             :node  child-node})
               (+ index size)
               (next child-entries)))
      result)))

(defn deeper-path+text-index
  "Returns a [^js path text-index] expressed 1 level deeper.
   `affinity` is either :forward or :backward with semantic similar to Slate."
  [^js editor ^js path text-index affinity]
  (let [[child-index text-index] (loop [child-index 0
                                        text-index text-index
                                        children (children-nodes-info editor path)]
                                   (if (seq children)
                                     (let [{:keys [size]} (first children)
                                           next-text-index (- text-index size)
                                           keep-walking (case affinity
                                                          :backward (> next-text-index 0)
                                                          :forward (>= next-text-index 0))]
                                       (if keep-walking
                                         (recur (inc child-index)
                                                next-text-index
                                                (next children))
                                         [child-index text-index]))
                                     [child-index text-index]))]
    [(doto (aclone path)
       (.push child-index))
     text-index]))
